export const STUDIO_BUILDER_VERSION = "a21.2_v1" as const;

export type StudioCapabilityDefinition<TId extends string = string> = {
  id: TId;
  label: string;
  description: string;
};

export const STUDIO_SUPPORTED_CAPABILITIES = [
  {
    id: "hours_location",
    label: "Hours and location answers",
    description: "Answer safe front-desk questions about hours, address, phone, and clinic access basics.",
  },
  {
    id: "scheduling_guidance",
    label: "Scheduling guidance",
    description: "Guide public booking flows using the current owner-confirmed routing truth without changing runtime behavior.",
  },
  {
    id: "renewal_guidance",
    label: "Renewal guidance",
    description: "Explain renewal preparation and next-step guidance without crossing into medical advice.",
  },
  {
    id: "staff_handoff",
    label: "Staff handoff",
    description: "Collect clean context and escalate to staff when the request leaves safe front-desk scope.",
  },
  {
    id: "callback_capture",
    label: "Callback capture",
    description: "Capture callback preferences for staff follow-up without widening PHI or payment scope.",
  },
  {
    id: "preview_demo",
    label: "Preview and demo support",
    description: "Support internal preview, demo-driver use, and sandbox rehearsal only.",
  },
] as const satisfies readonly StudioCapabilityDefinition[];

export type StudioSupportedCapabilityId =
  (typeof STUDIO_SUPPORTED_CAPABILITIES)[number]["id"];

export const STUDIO_BLOCKED_CAPABILITIES = [
  {
    id: "medical_advice",
    label: "Medical advice or diagnosis",
    description: "Studio cannot recommend diagnosis, symptom analysis, treatment plans, or clinical advice.",
  },
  {
    id: "dosage_guidance",
    label: "Dosage or medication guidance",
    description: "Studio cannot suggest what medication to take or how much to take.",
  },
  {
    id: "live_clinic_activation",
    label: "Live clinic deployment",
    description: "Studio cannot turn on inbound receptionist, outbound scheduling, or other live clinic lanes.",
  },
  {
    id: "booking_or_webhook_changes",
    label: "Booking or webhook changes",
    description: "Studio cannot rewrite booking logic, routing truth, or webhook behavior.",
  },
  {
    id: "payment_or_sensitive_phi_collection",
    label: "Payment or sensitive PHI collection",
    description: "Studio cannot collect card details, full DOB workflows, or other sensitive front-desk data expansions.",
  },
] as const satisfies readonly StudioCapabilityDefinition[];

export type StudioBlockedCapabilityId =
  (typeof STUDIO_BLOCKED_CAPABILITIES)[number]["id"];

export type StudioBuilderInput = {
  operatorDescription: string;
};

export type StudioCapabilityRecommendation = {
  id: StudioSupportedCapabilityId;
  label: string;
  reason: string;
};

export type StudioBlockedCapabilitySuggestion = {
  id: StudioBlockedCapabilityId;
  label: string;
  reason: string;
  detectedFromInput: boolean;
};

export type StudioGeneratedAssistantBrief = {
  version: typeof STUDIO_BUILDER_VERSION;
  mode: "deterministic_policy";
  input: {
    operatorDescription: string;
    normalizedDescription: string;
  };
  assistantName: string;
  description: string;
  starterTone: string;
  starterPrompt: string;
  recommendedCapabilities: StudioCapabilityRecommendation[];
  blockedCapabilities: StudioBlockedCapabilitySuggestion[];
  readinessNotes: string[];
};
